import React, { FunctionComponent, useCallback, useRef, useState } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { mobileCSS } from '@measures/responsive'
import { LinkCarouselProps } from '@widgets/LinkCarousel/types'
import LinkCarouselLabel from './LinkCarouselLabel'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import SectionHeader from '@components/SectionHeader'
import NavigationArrows from '@components/Swiper/NavigationArrows'
import { ArrowsVisibilityType, SwiperClass } from '@hooks/useSwiper'
import LinkCarouselTile from '@components/LinkCarousel/LinkCarouselTile'
import Swiper from '@components/Swiper'

const StyledLinkCarousel = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      measures: { outerPadding },
      spacing: { spacing16 },
    },
  }) => css`
    margin-top: ${spacing16};
    ${mobileCSS(css`
      margin-right: -${outerPadding.mobile}; // + stretch to right for mobile
      width: auto;

      > .swiper-items-container > *:last-child {
        margin-right: ${outerPadding.mobile};
        scroll-margin-right: ${outerPadding.mobile};
      }
    `)}
  `}
`
const LinkCarouselSections: FunctionComponent<LinkCarouselProps> = ({
  hideTitle,
  label,
  link,
  tiles,
  title,
}) => {
  const swiperRef = useRef<SwiperClass | null>(null)
  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>()

  const onInit = useCallback(
    (swiper: any) => {
      if (swiperRef) {
        swiperRef.current = swiper
      }
    },
    [swiperRef]
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  const onImpressionTrackingFn = useCallback<TrackingFnType>(
    () => ({
      event: 'linkcarousel_impression',
      eventAction: 'impression',
      eventCategory: 'linkcarousel_widget',
      eventLabel: title,
      linkcarousel_title: title,
    }),
    [title]
  )

  const onImpression = useTracking(onImpressionTrackingFn)

  const viewportRef = useViewportTracking({
    onImpression,
    track: true,
  })

  const theme = useTheme()

  return (
    <StyledLinkCarousel aria-label={title} ref={viewportRef}>
      {!hideTitle && (
        <SectionHeader title={title} accentColor={theme.color.secondary.green}>
          {arrowsVisibility && arrowsVisibility !== 'none' && (
            <NavigationArrows
              swiperRef={swiperRef}
              arrowsVisibility={arrowsVisibility}
            />
          )}
        </SectionHeader>
      )}
      <StyledSwiper
        initialSlide={0}
        spaceTopBottom={0}
        slidesPerGroup={1}
        spaceBetween={16}
        onInit={onInit}
        onArrowsVisibility={onArrowsVisibility}
        slides={tiles.map((tile, index) => (
          <LinkCarouselTile
            key={`${tile.title}-${index}`}
            mainTitle={title}
            {...tile}
          />
        ))}
      />
      <LinkCarouselLabel label={label} link={link} />
    </StyledLinkCarousel>
  )
}

export default LinkCarouselSections
